/**
 * ZandiMapPage - Submarket Intelligence & Backtesting Visualization
 */

import { useCallback } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { Map, TrendingUp } from 'lucide-react';
import { SectionTitle } from '../components/Luxury';
import {
  SubmarketDetailPanel,
  MethodologySection,
  ScoreFilterBar,
  RentGrowthRankingsTable,
} from '../components/ZandiMap';
import { GoogleSubmarketLayer, type GoogleSubmarketProperties } from '../components/Map';
import { useSubmarketStore } from '../store/useSubmarketStore';
import { useActiveMarket } from '../store/useMarketStore';
import { useGoogleMaps } from '../providers/GoogleMapsProvider';
import { Breadcrumbs } from '../components/Layout/Breadcrumbs';

/**
 * Dark map style for the Zandi map - matches the dark theme.
 */
const darkMapStyle: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1d1d1d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

export function ZandiMapPage() {
  const { selectedSubmarketId, setSelectedSubmarketId } = useSubmarketStore();
  const market = useActiveMarket();
  const { isLoaded, loadError } = useGoogleMaps();

  const mapCenter = { lat: market.center_lat, lng: market.center_lng };

  /**
   * Handle submarket polygon click - opens detail panel.
   */
  const handleSubmarketClick = useCallback(
    (submarket: GoogleSubmarketProperties) => {
      setSelectedSubmarketId(parseInt(submarket.submarket_id, 10));
    },
    [setSelectedSubmarketId]
  );

  return (
    <div className="flex flex-col min-h-full bg-obsidian overflow-y-auto">
      {/* Header */}
      <div className="flex-shrink-0 bg-charcoal border-b border-slate-dark px-6 py-4">
        <Breadcrumbs className="mb-3" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <SectionTitle icon={<Map className="w-5 h-5" />}>
              Submarket Intelligence
            </SectionTitle>
            <span className="text-stone-500 text-sm">
              {market.submarket_count} {market.display_name} Submarkets | Backtested Scoring
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <TrendingUp className="w-4 h-4 text-emerald" />
            <span className="text-ivory">+0.438 Correlation</span>
            <span className="text-stone-400">with 5-year returns</span>
          </div>
        </div>
      </div>

      {/* Main Content - Map Container */}
      <div className="relative" style={{ height: '60vh', minHeight: 400 }}>
        {/* Full-screen choropleth map */}
        <div className="absolute inset-0">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={11}
              options={{
                styles: darkMapStyle,
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: true,
              }}
            >
              <GoogleSubmarketLayer
                onSubmarketClick={handleSubmarketClick}
                highlightedSubmarket={null}
              />
            </GoogleMap>
          ) : loadError ? (
            <div className="flex items-center justify-center h-full bg-graphite">
              <p className="text-crimson">Failed to load Google Maps</p>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full bg-graphite">
              <Map className="w-16 h-16 text-stone-300 mx-auto animate-pulse" />
            </div>
          )}
        </div>

        {/* Score Filter Bar */}
        <div className="absolute top-4 left-4 z-10">
          <ScoreFilterBar />
        </div>

        {/* Submarket Detail Panel */}
        {selectedSubmarketId && (
          <div className="absolute top-4 right-4 bottom-4 w-96 z-20">
            <SubmarketDetailPanel
              submarketId={selectedSubmarketId}
              onClose={() => setSelectedSubmarketId(null)}
            />
          </div>
        )}
      </div>

      {/* Rent Growth Rankings Table */}
      <div className="flex-shrink-0 px-6 py-6">
        <RentGrowthRankingsTable marketId={market.market_id} />
      </div>

      {/* Methodology Footer */}
      <div className="flex-shrink-0">
        <MethodologySection />
      </div>
    </div>
  );
}

export default ZandiMapPage;
